import { useState, useEffect } from "react";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Eyebrow } from "@/components/Eyebrow";
import { toast } from "@/components/ui/sonner";
import { copyText } from "@/lib/util";
import { Share2, Copy, ExternalLink, Loader2, EyeOff, Eye } from "lucide-react";

export function ShareKitDialog({ open, onClose, brandId, shareId, shareEnabled, views, onChange }) {
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(shareId || null);
  const [enabled, setEnabled] = useState(!!shareEnabled);

  useEffect(() => {
    if (open) { setSid(shareId || null); setEnabled(!!shareEnabled); }
  }, [open, shareId, shareEnabled]);

  const link = sid ? `${window.location.origin}/kit/${sid}` : "";

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/share`);
      setSid(data.shareId);
      setEnabled(true);
      onChange?.(data.shareId, true);
      toast.success("Sharing enabled");
    } catch (e) {
      toast.error(errMsg(e, "Could not enable sharing"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete(`/brands/${brandId}/share`);
      setEnabled(false);
      onChange?.(sid, false);
      toast("Sharing turned off");
    } catch (e) {
      toast.error(errMsg(e, "Could not turn off sharing"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ background: "var(--panel)", border: "1px solid var(--cardhover)", maxWidth: 460 }} data-testid="share-kit-dialog">
        <DialogTitle className="sr-only">Share brand kit</DialogTitle>
        <DialogDescription className="sr-only">Create a public read-only link to this brand kit.</DialogDescription>
        <Eyebrow>Share Brand Kit</Eyebrow>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: "rgba(249,115,22,0.12)", color: "var(--orange)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Share2 size={20} />
          </div>
          <p className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", lineHeight: 1.55, margin: 0 }}>
            Hand stakeholders a polished, read-only view of this brand's palette, voice and rendered assets — no login required.
          </p>
        </div>

        {enabled && sid ? (
          <>
            <div style={{ marginTop: 18 }}>
              <label className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Public link</label>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input data-testid="share-link-input" readOnly value={link} className="dim-input font-mono" style={{ fontSize: 12 }} onFocus={(e) => e.target.select()} />
              </div>
              {typeof views === "number" && (
                <div data-testid="share-views" className="font-mono" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--tmuted)", marginTop: 8 }}>
                  <Eye size={12} /> Viewed {views.toLocaleString()} {views === 1 ? "time" : "times"}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button data-testid="share-copy-link" className="btn-primary" style={{ flex: 1 }} onClick={() => copyText(link, "Link copied")}>
                <Copy size={14} /> Copy link
              </button>
              <a data-testid="share-open-link" className="btn-secondary" style={{ flex: 1, textDecoration: "none" }} href={link} target="_blank" rel="noreferrer">
                <ExternalLink size={14} /> Open
              </a>
            </div>
            <button data-testid="share-revoke" className="font-body" onClick={revoke} disabled={busy} style={{ marginTop: 16, background: "transparent", border: "none", color: "var(--tmuted)", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", gap: 6, justifyContent: "center", width: "100%" }}>
              {busy ? <Loader2 size={13} className="spin" /> : <EyeOff size={13} />} Turn off sharing
            </button>
          </>
        ) : (
          <button data-testid="share-enable" className="btn-primary" style={{ width: "100%", marginTop: 20, padding: 12 }} onClick={enable} disabled={busy}>
            {busy ? <Loader2 size={15} className="spin" /> : <Share2 size={15} />} Create public link
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
